(() => {
  document.documentElement.classList.remove('no-js');
  document.documentElement.classList.add('js');

  const root = window.Shopify?.routes?.root || '/';
  const drawer = document.querySelector('[data-cart-drawer]');
  const drawerBody = drawer?.querySelector('[data-cart-drawer-body]');
  const drawerFooter = drawer?.querySelector('[data-cart-drawer-footer]');
  const drawerUpsells = drawer?.querySelector('[data-cart-upsells]');
  const toast = document.querySelector('[data-toast]');

  const money = (cents) => {
    const currency = window.Shopify?.currency?.active || 'USD';
    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en', {
        style: 'currency', currency
      }).format(cents / 100);
    } catch (_) {
      return `$${(cents / 100).toFixed(2)}`;
    }
  };

  const escapeHtml = (value = '') => String(value)
    .replaceAll('&', '&amp;')
    .replaceAll('<', '&lt;')
    .replaceAll('>', '&gt;')
    .replaceAll('"', '&quot;')
    .replaceAll("'", '&#039;');

  const showToast = (message) => {
    if (!toast) return;
    toast.textContent = message;
    toast.hidden = false;
    window.clearTimeout(showToast.timer);
    showToast.timer = window.setTimeout(() => { toast.hidden = true; }, 2600);
  };

  const updateCount = (count) => {
    document.querySelectorAll('[data-cart-count]').forEach((node) => { node.textContent = count; });
  };

  const renderCart = (cart) => {
    if (!drawerBody || !drawerFooter) return;
    updateCount(cart.item_count);

    if (drawerUpsells) {
      const productIds = new Set(cart.items.map((item) => String(item.product_id)));
      let visibleUpsells = 0;
      drawerUpsells.querySelectorAll('[data-upsell-product]').forEach((card) => {
        const isInCart = productIds.has(String(card.dataset.upsellProduct));
        card.hidden = isInCart;
        if (!isInCart) visibleUpsells += 1;
      });
      drawerUpsells.hidden = cart.item_count === 0 || visibleUpsells === 0;
    }

    if (!cart.items.length) {
      drawerBody.innerHTML = `
        <div class="rm-empty-state">
          <h2>Your cart is empty.</h2>
          <p>Find a world your players will remember.</p>
          <a class="rm-button rm-button--accent" href="${escapeHtml(root)}collections/all">Explore maps ↗</a>
        </div>`;
      drawerFooter.hidden = true;
      return;
    }

    drawerBody.innerHTML = cart.items.map((item, index) => `
      <article class="rm-cart-drawer__line">
        <a href="${escapeHtml(item.url)}">
          ${item.image ? `<img src="${escapeHtml(item.image)}&width=240" alt="${escapeHtml(item.product_title)}" width="120" height="100">` : ''}
        </a>
        <div>
          <small>${escapeHtml(item.vendor || 'RM Maps')}</small>
          <h3><a href="${escapeHtml(item.url)}">${escapeHtml(item.product_title)}</a></h3>
          ${item.variant_title ? `<small>${escapeHtml(item.variant_title)}</small>` : ''}
          <br><button class="rm-text-button" type="button" data-cart-remove="${index + 1}">Remove</button>
        </div>
        <strong>${money(item.final_line_price)}</strong>
      </article>`).join('');

    drawerFooter.hidden = false;
    const total = drawerFooter.querySelector('[data-cart-total]');
    if (total) total.textContent = money(cart.total_price);
  };

  const fetchCart = async () => {
    const response = await fetch(`${root}cart.js`, { headers: { Accept: 'application/json' } });
    if (!response.ok) throw new Error('Unable to load cart');
    const cart = await response.json();
    renderCart(cart);
    return cart;
  };

  const openCart = async () => {
    if (!drawer) return;
    if (!drawer.open) {
      if (typeof drawer.showModal === 'function') drawer.showModal();
      else drawer.setAttribute('open', '');
    }
    document.body.classList.add('rm-lock');
    try { await fetchCart(); } catch (_) { showToast('Could not load your cart.'); }
  };

  const closeCart = () => {
    if (!drawer) return;
    if (typeof drawer.close === 'function') drawer.close();
    else drawer.removeAttribute('open');
    document.body.classList.remove('rm-lock');
  };

  document.addEventListener('click', async (event) => {
    if (event.target.closest('[data-cart-open]')) {
      event.preventDefault();
      openCart();
      return;
    }

    if (event.target.closest('[data-cart-close]')) {
      closeCart();
      return;
    }

    const remove = event.target.closest('[data-cart-remove]');
    if (remove) {
      remove.disabled = true;
      try {
        const response = await fetch(`${root}cart/change.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ line: Number(remove.dataset.cartRemove), quantity: 0 })
        });
        if (!response.ok) throw new Error();
        renderCart(await response.json());
      } catch (_) {
        remove.disabled = false;
        showToast('Could not remove this item.');
      }
      return;
    }

    const menuToggle = event.target.closest('[data-menu-toggle]');
    const menuClose = event.target.closest('[data-menu-close]');
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    if (menuToggle && mobileMenu) {
      mobileMenu.hidden = false;
      menuToggle.setAttribute('aria-expanded', 'true');
      document.body.classList.add('rm-lock');
    }
    if (menuClose && mobileMenu) {
      mobileMenu.hidden = true;
      document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('rm-lock');
    }

    const minus = event.target.closest('[data-quantity-minus]');
    const plus = event.target.closest('[data-quantity-plus]');
    if (minus || plus) {
      const field = event.target.closest('[data-quantity]')?.querySelector('input');
      if (!field) return;
      const current = Number(field.value) || 1;
      field.value = minus ? Math.max(Number(field.min) || 1, current - 1) : current + 1;
    }
  });

  document.addEventListener('keydown', (event) => {
    if (event.key !== 'Escape') return;
    closeCart();
    const mobileMenu = document.querySelector('[data-mobile-menu]');
    if (mobileMenu && !mobileMenu.hidden) {
      mobileMenu.hidden = true;
      document.querySelector('[data-menu-toggle]')?.setAttribute('aria-expanded', 'false');
      document.body.classList.remove('rm-lock');
    }
  });

  document.addEventListener('submit', async (event) => {
    const form = event.target.closest('[data-product-form]');
    if (!form) return;
    event.preventDefault();
    const submit = form.querySelector('[data-add-to-cart]');
    submit?.setAttribute('disabled', '');
    form.classList.add('is-loading');

    try {
      const response = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: new FormData(form)
      });
      const data = await response.json();
      if (!response.ok) throw new Error(data.description || 'Could not add to cart');
      await openCart();
      showToast(`${data.product_title || 'Product'} added to cart.`);
    } catch (error) {
      showToast(error.message || 'Could not add to cart.');
    } finally {
      submit?.removeAttribute('disabled');
      form.classList.remove('is-loading');
    }
  });

  document.querySelectorAll('[data-variant-select]').forEach((select) => {
    select.addEventListener('change', () => {
      const rootNode = select.closest('[data-product-root]');
      const variants = JSON.parse(rootNode.querySelector('[data-product-json]')?.textContent || '[]');
      const variant = variants.find((item) => String(item.id) === select.value);
      if (!variant) return;
      const priceNode = rootNode.querySelector('.rm-price__current');
      const submit = rootNode.querySelector('[data-add-to-cart]');
      if (priceNode) priceNode.textContent = money(variant.price);
      if (submit) {
        submit.disabled = !variant.available;
        const label = submit.querySelector('span');
        if (label) label.textContent = variant.available ? 'Add to cart' : 'Sold out';
      }
      const featuredMediaId = variant.featured_media?.id || variant.featured_image?.id;
      if (featuredMediaId) rootNode.querySelector(`[data-product-media-button="${featuredMediaId}"]`)?.click();
      const url = new URL(window.location.href);
      url.searchParams.set('variant', variant.id);
      window.history.replaceState({}, '', url);
    });
  });

  document.querySelectorAll('[data-sort-select]').forEach((select) => {
    select.addEventListener('change', () => select.form.submit());
  });

  const setupSliders = (scope = document) => {
    scope.querySelectorAll('[data-slider]:not([data-slider-ready])').forEach((slider) => {
      slider.dataset.sliderReady = 'true';
      const track = slider.querySelector('[data-slider-track]');
      const slides = [...slider.querySelectorAll('[data-slide]')];
      if (!track || !slides.length) return;

      let current = 0;
      let pointerStart = null;

      const update = (index) => {
        current = (index + slides.length) % slides.length;
        track.style.transform = `translate3d(-${current * 100}%, 0, 0)`;
        slides.forEach((slide, slideIndex) => {
          const isCurrent = slideIndex === current;
          slide.setAttribute('aria-hidden', isCurrent ? 'false' : 'true');
          slide.toggleAttribute('inert', !isCurrent);
        });
      };

      slider.addEventListener('click', (event) => {
        const previousButton = event.target.closest('[data-slider-prev]');
        const nextButton = event.target.closest('[data-slider-next]');
        if (!previousButton && !nextButton) return;
        event.preventDefault();
        event.stopPropagation();
        update(current + (nextButton ? 1 : -1));
      });

      track.addEventListener('pointerdown', (event) => {
        if (event.target.closest('button, a, input, select, textarea')) return;
        pointerStart = event.clientX;
        track.setPointerCapture?.(event.pointerId);
      });
      track.addEventListener('pointerup', (event) => {
        if (pointerStart === null) return;
        const distance = event.clientX - pointerStart;
        if (Math.abs(distance) > 45) update(current + (distance < 0 ? 1 : -1));
        pointerStart = null;
      });

      slider.addEventListener('keydown', (event) => {
        if (event.key === 'ArrowLeft') update(current - 1);
        if (event.key === 'ArrowRight') update(current + 1);
      });

      update(0);
    });
  };

  const setupCategoryCards = (scope = document) => {
    scope.querySelectorAll('[data-category-card]:not([data-category-ready])').forEach((card) => {
      card.dataset.categoryReady = 'true';
      card.addEventListener('pointermove', (event) => {
        const bounds = card.getBoundingClientRect();
        card.style.setProperty('--rm-x', `${event.clientX - bounds.left}px`);
        card.style.setProperty('--rm-y', `${event.clientY - bounds.top}px`);
      });
    });
  };

  const setupProductCards = (scope = document) => {
    scope.querySelectorAll('[data-product-card-ready], .rm-product-card:not([data-product-card-ready])').forEach((card) => {
      if (card.dataset.productCardReady) return;
      card.dataset.productCardReady = 'true';
      if (!window.matchMedia('(hover: hover) and (pointer: fine)').matches) return;

      const reset = () => {
        card.style.setProperty('--rm-card-rx', '0deg');
        card.style.setProperty('--rm-card-ry', '0deg');
        card.style.setProperty('--rm-card-image-x', '0px');
        card.style.setProperty('--rm-card-image-y', '0px');
      };

      card.addEventListener('pointermove', (event) => {
        const bounds = card.getBoundingClientRect();
        const relativeX = (event.clientX - bounds.left) / bounds.width;
        const relativeY = (event.clientY - bounds.top) / bounds.height;
        const x = relativeX - .5;
        const y = relativeY - .5;
        card.style.setProperty('--rm-card-rx', `${y * -5}deg`);
        card.style.setProperty('--rm-card-ry', `${x * 7}deg`);
        card.style.setProperty('--rm-card-image-x', `${x * 9}px`);
        card.style.setProperty('--rm-card-image-y', `${y * 7}px`);
        card.style.setProperty('--rm-card-x', `${relativeX * 100}%`);
        card.style.setProperty('--rm-card-y', `${relativeY * 100}%`);
      });
      card.addEventListener('pointerleave', reset);
      card.addEventListener('blur', reset, true);
    });
  };

  const setupProductPages = (scope = document) => {
    scope.querySelectorAll('[data-product-page]:not([data-product-page-ready])').forEach((page) => {
      page.dataset.productPageReady = 'true';
      const gallery = page.querySelector('[data-product-media-gallery]');
      const stage = gallery?.querySelector('.rm-product-master__stage');
      const panels = [...(gallery?.querySelectorAll('[data-product-media-panel]') || [])];
      const buttons = [...(gallery?.querySelectorAll('[data-product-media-button]') || [])];
      const count = gallery?.querySelector('[data-product-media-current]');
      const trailerPanel = gallery?.querySelector('[data-product-trailer-panel]');
      const trailerToggle = gallery?.querySelector('[data-product-trailer-toggle]');
      const trailerLabel = trailerToggle?.querySelector('[data-product-trailer-label]');
      const trailerIframe = trailerPanel?.querySelector('iframe');
      const trailerBaseSrc = trailerIframe?.getAttribute('src') || '';
      if (!gallery || !panels.length) return;

      let active = Math.max(0, panels.findIndex((panel) => !panel.hidden));
      let previousMedia = active;
      const trailerIndex = trailerPanel ? panels.indexOf(trailerPanel) : -1;

      const activate = (index, scrollToGallery = false) => {
        active = (index + panels.length) % panels.length;
        const trailerIsActive = active === trailerIndex;
        if (!trailerIsActive) previousMedia = active;
        panels.forEach((panel, panelIndex) => {
          const isActive = panelIndex === active;
          panel.hidden = !isActive;
          panel.setAttribute('aria-hidden', isActive ? 'false' : 'true');
          if (!isActive) panel.querySelectorAll('video').forEach((video) => video.pause());
        });
        const mediaId = panels[active].dataset.productMediaPanel;
        buttons.forEach((button) => button.setAttribute('aria-current', button.dataset.productMediaButton === mediaId ? 'true' : 'false'));
        if (count && !trailerIsActive) count.textContent = String(active + 1).padStart(2, '0');
        stage?.classList.toggle('is-trailer', trailerIsActive);
        if (trailerToggle) trailerToggle.setAttribute('aria-pressed', trailerIsActive ? 'true' : 'false');
        if (trailerLabel && trailerToggle) trailerLabel.textContent = trailerIsActive ? trailerToggle.dataset.backLabel : trailerToggle.dataset.watchLabel;
        if (trailerIframe && trailerBaseSrc) {
          if (trailerIsActive) {
            if (!trailerIframe.src.includes('autoplay=1')) trailerIframe.src = `${trailerBaseSrc}${trailerBaseSrc.includes('?') ? '&' : '?'}autoplay=1`;
          } else if (trailerIframe.getAttribute('src') !== trailerBaseSrc) {
            trailerIframe.setAttribute('src', trailerBaseSrc);
          }
        }
        buttons.find((button) => button.dataset.productMediaButton === mediaId)?.scrollIntoView({ behavior: 'smooth', block: 'nearest', inline: 'nearest' });
        if (scrollToGallery) gallery.scrollIntoView({ behavior: 'smooth', block: 'center' });
      };

      buttons.forEach((button) => button.addEventListener('click', () => {
        const index = panels.findIndex((panel) => panel.dataset.productMediaPanel === button.dataset.productMediaButton);
        if (index >= 0) activate(index);
      }));
      trailerToggle?.addEventListener('click', () => activate(active === trailerIndex ? previousMedia : trailerIndex));
      page.querySelectorAll('[data-product-story-media]').forEach((button) => button.addEventListener('click', () => {
        const index = panels.findIndex((panel) => panel.dataset.productMediaPanel === button.dataset.productStoryMedia);
        if (index >= 0) activate(index, true);
      }));

      if (stage && window.matchMedia('(hover: hover) and (pointer: fine)').matches) {
        stage.addEventListener('pointermove', (event) => {
          const bounds = stage.getBoundingClientRect();
          const x = (event.clientX - bounds.left) / bounds.width;
          const y = (event.clientY - bounds.top) / bounds.height;
          stage.style.setProperty('--rm-product-x', `${x * 100}%`);
          stage.style.setProperty('--rm-product-y', `${y * 100}%`);
          stage.style.setProperty('--rm-product-image-x', `${(x - .5) * 8}px`);
          stage.style.setProperty('--rm-product-image-y', `${(y - .5) * 6}px`);
        });
        stage.addEventListener('pointerleave', () => {
          stage.style.setProperty('--rm-product-image-x', '0px');
          stage.style.setProperty('--rm-product-image-y', '0px');
        });
      }
      activate(active);
    });
  };

  const setupProductShowcases = (scope = document) => {
    scope.querySelectorAll('[data-product-showcase]:not([data-product-showcase-ready])').forEach((showcase) => {
      showcase.dataset.productShowcaseReady = 'true';
      const carousel = showcase.querySelector('[data-product-showcase-carousel]');
      if (carousel) {
        const viewport = carousel.querySelector('[data-product-showcase-viewport]');
        const slides = [...carousel.querySelectorAll('[data-product-showcase-slide]')];
        const current = carousel.querySelector('[data-showcase-current]');
        const progress = carousel.querySelector('[data-showcase-progress]');
        let active = Math.max(0, slides.findIndex((slide) => slide.classList.contains('is-active')));
        let pointerStart = null;
        let pointerDelta = 0;
        let pendingDragDistance = 0;
        let dragFrame = 0;
        let wheelLocked = false;

        const hydrateSlide = (slide) => {
          const image = slide?.querySelector('img[data-showcase-src]');
          if (!image) return;
          if (image.dataset.showcaseSrcset) image.srcset = image.dataset.showcaseSrcset;
          image.src = image.dataset.showcaseSrc;
          image.removeAttribute('data-showcase-src');
          image.removeAttribute('data-showcase-srcset');
        };

        const hydrateAround = (index, radius = 2) => {
          if (!slides.length) return;
          for (let distance = -radius; distance <= radius; distance += 1) {
            hydrateSlide(slides[(index + distance + slides.length) % slides.length]);
          }
        };

        const circularOffset = (slideIndex) => {
          let offset = slideIndex - active;
          const half = slides.length / 2;
          if (offset > half) offset -= slides.length;
          if (offset < -half) offset += slides.length;
          return offset;
        };

        const setDragDistance = (dragDistance = 0) => {
          pendingDragDistance = dragDistance;
          if (dragFrame) return;
          dragFrame = window.requestAnimationFrame(() => {
            dragFrame = 0;
            const viewportWidth = viewport?.clientWidth || 1;
            const dragPercent = Math.max(-42, Math.min(42, (pendingDragDistance / viewportWidth) * 120));
            carousel.style.setProperty('--rm-depth-drag', `${dragPercent}%`);
          });
        };

        const renderDepth = () => {
          slides.forEach((slide, slideIndex) => {
            const offset = circularOffset(slideIndex);
            const distance = Math.abs(offset);
            const direction = Math.sign(offset);
            const x = distance === 0 ? 0 : direction * (64 + ((distance - 1) * 49));
            slide.style.setProperty('--rm-depth-x', `${x}%`);
            slide.style.setProperty('--rm-depth-z', `${distance * -155}px`);
            slide.style.setProperty('--rm-depth-rotate', `${offset * -8.5}deg`);
            slide.style.setProperty('--rm-depth-scale', String(Math.max(.5, 1 - (distance * .135))));
            slide.style.setProperty('--rm-depth-opacity', String(distance > 4 ? 0 : Math.max(.38, 1 - (distance * .13))));
            slide.style.setProperty('--rm-depth-zindex', String(20 - distance));
            slide.classList.toggle('is-active', distance === 0);
            slide.classList.toggle('is-depth-near', distance <= 2);
            slide.classList.toggle('is-depth-hidden', distance > 2);
            slide.setAttribute('aria-hidden', distance === 0 ? 'false' : 'true');
          });
        };

        const selectSlide = (index) => {
          if (!slides.length) return;
          active = (index + slides.length) % slides.length;
          hydrateAround(active);
          if (dragFrame) {
            window.cancelAnimationFrame(dragFrame);
            dragFrame = 0;
          }
          pendingDragDistance = 0;
          carousel.style.setProperty('--rm-depth-drag', '0%');
          renderDepth();
          if (current) current.textContent = String(active + 1).padStart(2, '0');
          if (progress) progress.style.width = `${((active + 1) / slides.length) * 100}%`;
        };

        carousel.querySelector('[data-showcase-previous]')?.addEventListener('click', () => selectSlide(active - 1));
        carousel.querySelector('[data-showcase-next]')?.addEventListener('click', () => selectSlide(active + 1));
        carousel.addEventListener('rm:showcase:select', (event) => selectSlide(Number(event.detail?.index) || 0));

        viewport?.addEventListener('keydown', (event) => {
          if (event.key === 'ArrowLeft') {
            event.preventDefault();
            selectSlide(active - 1);
          }
          if (event.key === 'ArrowRight') {
            event.preventDefault();
            selectSlide(active + 1);
          }
          if (event.key === 'Home') selectSlide(0);
          if (event.key === 'End') selectSlide(slides.length - 1);
        });

        viewport?.addEventListener('pointerdown', (event) => {
          if (event.button !== 0 || event.target.closest('button')) return;
          event.preventDefault();
          pointerStart = event.clientX;
          pointerDelta = 0;
          viewport.classList.add('is-dragging');
          viewport.setPointerCapture?.(event.pointerId);
        });
        viewport?.addEventListener('dragstart', (event) => event.preventDefault());
        viewport?.querySelectorAll('img').forEach((image) => {
          image.draggable = false;
          image.setAttribute('draggable', 'false');
        });
        viewport?.addEventListener('pointerup', (event) => {
          if (pointerStart === null) return;
          const distance = event.clientX - pointerStart;
          pointerStart = null;
          viewport.classList.remove('is-dragging');
          if (Math.abs(distance) >= 46) selectSlide(active + (distance < 0 ? 1 : -1));
          else setDragDistance(0);
          window.setTimeout(() => { pointerDelta = 0; }, 0);
        });
        viewport?.addEventListener('pointercancel', () => {
          pointerStart = null;
          pointerDelta = 0;
          viewport.classList.remove('is-dragging');
          setDragDistance(0);
        });
        viewport?.addEventListener('pointermove', (event) => {
          if (pointerStart !== null) {
            pointerDelta = event.clientX - pointerStart;
            setDragDistance(pointerDelta);
          }
        }, { passive: true });
        viewport?.addEventListener('wheel', (event) => {
          const horizontalIntent = Math.abs(event.deltaX) > Math.abs(event.deltaY) || event.shiftKey;
          if (!horizontalIntent) return;
          event.preventDefault();
          if (wheelLocked) return;
          const direction = (event.deltaX || event.deltaY) > 0 ? 1 : -1;
          selectSlide(active + direction);
          wheelLocked = true;
          window.setTimeout(() => { wheelLocked = false; }, 420);
        }, { passive: false });
        slides.forEach((slide, slideIndex) => slide.addEventListener('click', () => {
          if (Math.abs(pointerDelta) < 8 && slideIndex !== active) selectSlide(slideIndex);
        }));

        selectSlide(active);
      }
      const ambientObserver = 'IntersectionObserver' in window ? new IntersectionObserver((entries) => {
        entries.forEach((entry) => showcase.classList.toggle('is-showcase-active', entry.isIntersecting));
      }, { rootMargin: '160px 0px' }) : null;
      if (ambientObserver) ambientObserver.observe(showcase);
      else showcase.classList.add('is-showcase-active');
      showcase.querySelectorAll('[data-product-showcase-range]').forEach((range) => {
        const comparison = range.closest('.rm-product-showcase__compare');
        const update = () => comparison?.style.setProperty('--rm-showcase-position', `${range.value}%`);
        range.addEventListener('input', update);
        update();
      });
      showcase.querySelectorAll('[data-product-video-play]').forEach((button) => {
        button.addEventListener('click', () => {
          const video = button.closest('[data-product-showcase-video]');
          const iframe = video?.querySelector('iframe');
          if (iframe) {
            const src = iframe.getAttribute('src') || '';
            if (!src.includes('autoplay=1')) iframe.setAttribute('src', `${src}${src.includes('?') ? '&' : '?'}autoplay=1`);
          }
          button.hidden = true;
        });
      });
    });
  };

  const setupCountUps = (scope = document) => {
    scope.querySelectorAll('[data-count-up-section]:not([data-count-up-ready])').forEach((section) => {
      section.dataset.countUpReady = 'true';
      const values = [...section.querySelectorAll('[data-count-up]')];
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;

      const run = () => {
        section.classList.add('is-visible');
        values.forEach((value, index) => {
          const original = value.textContent.trim();
          const match = original.match(/\d[\d\s,.]*/);
          if (!match || reduceMotion) return;
          const target = Number(match[0].replace(/[^\d]/g, ''));
          if (!Number.isFinite(target)) return;
          const prefix = original.slice(0, match.index);
          const suffix = original.slice(match.index + match[0].length);
          const separator = match[0].includes(',') ? ',' : (match[0].includes(' ') ? ' ' : '');
          const startTime = performance.now() + (index * 70);
          value.setAttribute('aria-label', original);

          const format = (number) => {
            const rounded = Math.round(number).toString();
            return separator ? rounded.replace(/\B(?=(\d{3})+(?!\d))/g, separator) : rounded;
          };
          const tick = (time) => {
            const progress = Math.min(Math.max((time - startTime) / 1200, 0), 1);
            const eased = 1 - Math.pow(1 - progress, 3);
            value.textContent = `${prefix}${format(target * eased)}${suffix}`;
            if (progress < 1) requestAnimationFrame(tick);
          };
          requestAnimationFrame(tick);
        });
      };

      if (reduceMotion || !('IntersectionObserver' in window)) {
        run();
        return;
      }
      const observer = new IntersectionObserver((entries) => {
        if (!entries.some((entry) => entry.isIntersecting)) return;
        observer.disconnect();
        run();
      }, { threshold: 0.35 });
      observer.observe(section);
    });
  };

  const setupPassInteractions = (scope = document) => {
    scope.querySelectorAll('.rm-passes:not([data-pass-ready])').forEach((section) => {
      section.dataset.passReady = 'true';
      const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const reveal = () => section.classList.add('is-inview');

      if (reduceMotion || !('IntersectionObserver' in window)) reveal();
      else {
        const observer = new IntersectionObserver((entries) => {
          if (!entries.some((entry) => entry.isIntersecting)) return;
          observer.disconnect();
          reveal();
        }, { threshold: 0.22 });
        observer.observe(section);
      }

      if (reduceMotion) return;
      section.querySelectorAll('.rm-pass-card__media').forEach((media) => {
        media.addEventListener('pointermove', (event) => {
          if (event.pointerType === 'touch') return;
          const bounds = media.getBoundingClientRect();
          const x = (event.clientX - bounds.left) / bounds.width - 0.5;
          const y = (event.clientY - bounds.top) / bounds.height - 0.5;
          media.style.setProperty('--rm-pass-x', `${x * 12}px`);
          media.style.setProperty('--rm-pass-y', `${y * 9}px`);
          media.style.setProperty('--rm-pass-rx', `${y * -3}deg`);
          media.style.setProperty('--rm-pass-ry', `${x * 4}deg`);
        });
        media.addEventListener('pointerleave', () => {
          media.style.setProperty('--rm-pass-x', '0px');
          media.style.setProperty('--rm-pass-y', '0px');
          media.style.setProperty('--rm-pass-rx', '0deg');
          media.style.setProperty('--rm-pass-ry', '0deg');
        });
      });
    });
  };

  const setupEditorialInteractions = (scope = document) => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    scope.querySelectorAll('[data-editorial-media]:not([data-editorial-ready])').forEach((media) => {
      media.dataset.editorialReady = 'true';
      if (reduceMotion) return;

      media.addEventListener('pointermove', (event) => {
        if (event.pointerType === 'touch') return;
        const bounds = media.getBoundingClientRect();
        const positionX = Math.min(Math.max((event.clientX - bounds.left) / bounds.width, 0), 1);
        const positionY = Math.min(Math.max((event.clientY - bounds.top) / bounds.height, 0), 1);
        const x = positionX - 0.5;
        const y = positionY - 0.5;
        media.style.setProperty('--rm-editorial-x', `${x * 14}px`);
        media.style.setProperty('--rm-editorial-y', `${y * 10}px`);
        media.style.setProperty('--rm-editorial-rx', `${y * -3.5}deg`);
        media.style.setProperty('--rm-editorial-ry', `${x * 4.5}deg`);
        media.style.setProperty('--rm-editorial-glow-x', `${positionX * 100}%`);
        media.style.setProperty('--rm-editorial-glow-y', `${positionY * 100}%`);
      });

      media.addEventListener('pointerleave', () => {
        media.style.setProperty('--rm-editorial-x', '0px');
        media.style.setProperty('--rm-editorial-y', '0px');
        media.style.setProperty('--rm-editorial-rx', '0deg');
        media.style.setProperty('--rm-editorial-ry', '0deg');
        media.style.setProperty('--rm-editorial-glow-x', '50%');
        media.style.setProperty('--rm-editorial-glow-y', '50%');
      });
    });
  };

  const setupMapComparisons = (scope = document) => {
    scope.querySelectorAll('[data-map-compare]:not([data-map-compare-ready])').forEach((compare) => {
      compare.dataset.mapCompareReady = 'true';
      const trigger = compare.querySelector('[data-map-picker-toggle]');
      const panel = compare.querySelector('[data-map-picker-panel]');
      const triggerTitle = compare.querySelector('[data-map-picker-title]');
      const triggerThumb = compare.querySelector('[data-map-picker-thumb]');
      const options = [...compare.querySelectorAll('[data-map-option]')];
      const slides = [...compare.querySelectorAll('[data-map-slide]')];

      const closePicker = () => {
        if (!panel || !trigger) return;
        panel.hidden = true;
        trigger.setAttribute('aria-expanded', 'false');
      };

      const chooseMapping = (index, focusStage = false) => {
        const option = options[index];
        const slide = slides[index];
        if (!option || !slide) return;

        options.forEach((item, itemIndex) => item.setAttribute('aria-selected', itemIndex === index ? 'true' : 'false'));
        slides.forEach((item, itemIndex) => { item.hidden = itemIndex !== index; });
        if (triggerTitle) triggerTitle.textContent = option.dataset.mapTitle || option.querySelector('strong')?.textContent || 'Choose a mapping';
        if (triggerThumb) {
          const visual = option.querySelector('img, svg');
          triggerThumb.replaceChildren();
          if (visual) triggerThumb.append(visual.cloneNode(true));
        }
        closePicker();
        if (focusStage) slide.querySelector('[data-compare-range]')?.focus({ preventScroll: true });
      };

      trigger?.addEventListener('click', () => {
        const willOpen = panel?.hidden;
        if (!panel) return;
        panel.hidden = !willOpen;
        trigger.setAttribute('aria-expanded', willOpen ? 'true' : 'false');
        if (willOpen) options.find((option) => option.getAttribute('aria-selected') === 'true')?.focus({ preventScroll: true });
      });

      options.forEach((option, index) => option.addEventListener('click', () => chooseMapping(index, true)));
      slides.forEach((slide) => {
        const range = slide.querySelector('[data-compare-range]');
        range?.addEventListener('input', () => slide.style.setProperty('--rm-compare-position', `${range.value}%`));
      });

      compare.addEventListener('keydown', (event) => {
        if (event.key === 'Escape') {
          closePicker();
          trigger?.focus();
        }
      });
      document.addEventListener('click', (event) => {
        if (!compare.contains(event.target)) closePicker();
      });

      if (options.length) chooseMapping(0);
    });
  };

  setupSliders();
  setupCategoryCards();
  setupProductCards();
  setupProductPages();
  setupProductShowcases();
  setupCountUps();
  setupPassInteractions();
  setupEditorialInteractions();
  setupMapComparisons();

  document.addEventListener('shopify:section:load', (event) => {
    setupSliders(event.target);
    setupCategoryCards(event.target);
    setupProductCards(event.target);
    setupProductPages(event.target);
    setupProductShowcases(event.target);
    setupCountUps(event.target);
    setupPassInteractions(event.target);
    setupEditorialInteractions(event.target);
    setupMapComparisons(event.target);
  });
  document.addEventListener('shopify:block:select', (event) => {
    const option = event.target.matches?.('[data-map-option]') ? event.target : event.target.querySelector?.('[data-map-option]');
    option?.click();
    const showcaseSlide = event.target.matches?.('[data-product-showcase-slide]') ? event.target : event.target.querySelector?.('[data-product-showcase-slide]');
    if (showcaseSlide) {
      showcaseSlide.closest('[data-product-showcase-carousel]')?.dispatchEvent(new CustomEvent('rm:showcase:select', {
        detail: { index: Number(showcaseSlide.dataset.showcaseIndex) || 0 }
      }));
    }
  });

  if (!customElements.get('product-recommendations')) {
    customElements.define('product-recommendations', class extends HTMLElement {
      connectedCallback() {
        const url = this.dataset.url;
        if (!url) return;
        fetch(url)
          .then((response) => response.text())
          .then((text) => {
            const html = new DOMParser().parseFromString(text, 'text/html');
            const content = html.querySelector('product-recommendations');
            if (content?.innerHTML.trim()) {
              this.innerHTML = content.innerHTML;
              setupProductCards(this);
            }
          })
          .catch(() => {});
      }
    });
  }

  fetchCart().catch(() => {});
})();
